import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { useDispatch, useSelector } from "react-redux";
import FlagIcon from "@mui/icons-material/Flag";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import type { ColDef } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import * as FileSaver from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Download, Rocket } from "lucide-react";
import moment from "moment";
import * as XLSX from "xlsx";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/Images/Delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/Images/EditIcon.svg";
import { ReactComponent as ExportFileIcon } from "../../../assets/Images/ExportPopupIcon.svg";
import { ReactComponent as SuccessIcon } from "../../../assets/Images/SuccessProjectIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { leadDataStyles } from "../../../assets/MUIStyles/leadDataStyle";
import { swalErrorMessage } from "../../../Constant/swalMessage";
import BaseButton from "../Common/Buttons/BaseButton";
import {
  converTime,
  PAGE_SIZE_50,
  TimestampConverter,
} from "../CommonComponents/Common";
import CommonModal from "../CommonComponents/Modal/CommonModal";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";
import DeletePopup from "../CommonComponents/PopUpCards/DeletePopUp";
import ResizableGrid from "../CommonComponents/ResizableGrid";
import MedicalReport from "../MedicalReciet/MedicalReciept";
import {
  celeryTaskPromoteAction,
  deleteLeadsAction,
  exportLeadsDataAction,
  getLeadsDataAction,
  updateLeadsAction,
} from "../Projects/Store/projects.action";
import { triggerAssessmentCallAction } from "../ProjectSection/CampaignSection/Store/Campaign.action";
import EditLeadModal from "./EditLeadModal";
import PatientTableFilters from "./PatientTableFilters";
import PatientTableHeader from "./PatientTableHeader";
import HistoryIcon from "@mui/icons-material/History";
import PatientHistoryBody from "./PatientHistory";
ModuleRegistry.registerModules([AllCommunityModule]);

const columnOrder = [
  { key: "_id", label: "Patient ID" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phoneNum", label: "Phone Number" },
  { key: "dateOfBirth", label: "Date of Birth" },
  { key: "insurance", label: "Insurance" },
  { key: "preExistingCondition", label: "Pre-existing Condition" },
  { key: "dateOfSurgery", label: "Date of Surgery" },
  { key: "pdfRec", label: "Record Type" },
  { key: "testsDone", label: "Test Results Received" },
  { key: "testsNotDone", label: "Tests Not Done" },
  { key: "actualTests", label: "All Tests Needed" },
  { key: "nameOfSurgeon", label: "Name Of Surgeon" },
  { key: "insights", label: "Insights" },
];

const formatHeaderName = (key: string) => {
  return (
    key
      ?.replace(/_/g, " ")
      ?.replace(/([a-z])([A-Z])/g, "$1 $2")
      ?.replace(/\b\w/g, (char) => char?.toUpperCase()) || ""
  );
};

const isDateLike = (val: string) => {
  if (typeof val !== "string") return false;
  const date = new Date(val.trim().replace("t", "T"));
  return !isNaN(date.getTime());
};

const autoCheckAllTestsDone = (formData) => {
  if (
    !formData?.actualTests ||
    !formData?.pdfRec ||
    formData?.pdfRec?.length === 0
  ) {
    return formData;
  }

  let requiredTestsArray = [];
  if (Array.isArray(formData?.actualTests)) {
    requiredTestsArray = formData.actualTests;
  } else if (formData?.actualTests) {
    try {
      requiredTestsArray = JSON.parse(formData.actualTests);
    } catch {
      requiredTestsArray = formData.actualTests
        .split(",")
        .map((test) => test.trim());
    }
  }

  const normalizedRequiredTests = requiredTestsArray
    .filter(Boolean)
    .map((test: string) => test.toLowerCase().trim());

  const completedTests = formData.pdfRec
    .filter((record) => record.response && record.response.trim() !== "")
    .map((record) => record.response.toLowerCase().trim());

  const allMatched = normalizedRequiredTests.every((test) =>
    completedTests.includes(test)
  );

  return {
    ...formData,
    allTestsDone: allMatched,
  };
};

const PatientTable = ({ patientObj }) => {
  const { patientState, setPatinetState, celeryData, fetchLeadsData } =
    patientObj;
  const { searchText, pageIndex } = patientState;
  const dispatch = useDispatch();
  const reducer = useSelector((reducer: any) => reducer);
  const {
    ProjectReducer: { leadsList: data },
  } = reducer;

  const [exportPopup, setExportPopup] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [patientPopup, setPatientPopup] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [viewRecord, setViewRecord] = useState([]);
  const [editRecordModal, setEditRecordModal] = useState(false);
  const [editButtonLoading, setEditButtonLoading] = useState(false);
  const [testDone, setTestDone] = useState("");
  const [workflowPopup, setWorkflowPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletePopUp, setDeletePopUp] = useState(false);
  const [confirmationPopUp, setConfirmationPopUp] = useState(false);
  const [patientId, setPatientId] = useState("");
  const [jobId, setJobId] = useState("");
  const [triggerPopUp, setTriggerPop] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [callHistory, setCallHistory] = useState([]);

  const [workflowTriggerAgainState, setWorkflowTriggerAgainState] = useState({
    showTriggerConfirm: false,
    triggerAgainLead: "",
    confirmButtonLoading: false,
  });
  const { showTriggerConfirm, triggerAgainLead, confirmButtonLoading } =
    workflowTriggerAgainState;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNum: "",
    dateOfBirth: "",
    insurance: "",
    preExistingCondition: "",
    dateOfSurgery: "",
    testsDone: "",
    testsNotDone: "",
    actualTests: [],
    nameOfSurgeon: "",
    emailOfSurgeon: "",
    phoneOfSurgeon: "",
    doNotContact: false,
    pdfRec: [],
    allTestsDone: false,
    arrivalTime: "",
    procedureTime: "",
  }) as any;

  const popoverOpen = Boolean(anchorEl);

  useEffect(() => {
    const updatedFormDataa = autoCheckAllTestsDone(formData);
    setFormData(updatedFormDataa);
    const testsDone = formData?.pdfRec
      ?.map((pdf) => pdf?.response?.trim())
      .filter(Boolean);

    const testsNotDone = formData.testsNotDone
      ? JSON.parse(formData.testsNotDone)
      : [];

    const updatedNotDone = testsNotDone.filter(
      (test) => !testsDone.includes(test?.trim())
    );

    const updatedFormData = {
      ...formData,
      testsDone: JSON.stringify(testsDone),
      testsNotDone: JSON.stringify(updatedNotDone),
    };

    setFormData(updatedFormData);
  }, [formData.actualTests, formData.pdfRec]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;
    if (name === "allTestsDone") {
      updatedValue = !formData?.allTestsDone;
    }
    if (name === "doNotContact") {
      updatedValue = value === "true";
    }
    setFormData((prevData: any) => ({
      ...prevData,
      [name]: updatedValue,
    }));
  };

  const handleSave = async () => {
    setEditButtonLoading(true);

    const data = {
      data: formData,
    };

    await updateLeadsAction(formData?._id, data);
    const params = {
      pageIndex,
      pageSize: PAGE_SIZE_50,
      searchtext: "",
    };
    await dispatch(getLeadsDataAction(params));
    setPatientPopup(true);
    setEditRecordModal(false);
    setEditButtonLoading(false);
  };

  const handleEdit = (lead) => {
    setEditRecordModal(true);
    setFormData(lead);
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const exportToCSV = async () => {
    setExportLoading(true);
    const params = {
      pageIndex: 1,
      pageSize: 1000,
      searchtext: "",
    };
    const res: any = await dispatch(exportLeadsDataAction(params));

    if (!res || res?.length === 0) {
      swalErrorMessage("Nothing to export.");
      setExportLoading(false);
      return;
    }
    const testKeys = new Set(["testsDone", "testsNotDone", "actualTests"]);
    const dateKeys = new Set(["dateOfBirth", "dateOfSurgery"]);
    const insightsKeys = [
      ...new Set(
        res?.flatMap((item) => item?.insights?.map((i) => i.key) || [])
      ),
    ];
    const arr: any = res?.map((lead) => {
      const obj: any = {};

      columnOrder.forEach(({ key, label }) => {
        if (dateKeys.has(key)) {
          obj[label] = lead[key]
            ? moment(lead[key]).format("DD-MM-YYYY")
            : "--";
        } else if (testKeys.has(key)) {
          const data = Array.isArray(lead[key])
            ? lead[key].join(", ")
            : lead[key]
            ? JSON.parse(lead[key])?.join(", ")
            : "";
          obj[label] = data;
        } else if (["pdfRec", "insights"]?.includes(key)) {
          return;
        } else {
          obj[label] = lead[key] || "--";
        }
      });

      insightsKeys?.forEach((key: any) => {
        obj[
          key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
        ] = lead?.insights?.find((item) => item?.key === key)?.value || "--";
      });

      // obj["Documents"] = Array.isArray(lead?.pdf) ? lead.pdf.join(", \r\n") : "";

      return obj;
    });

    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const ws = XLSX.utils.json_to_sheet(arr);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, `Patients` + fileExtension);

    setExportPopup(true);
    setExportLoading(false);
  };

  const handleDownloadPDF = async (patient) => {
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    document.body.appendChild(container);

    const root = createRoot(container);
    root.render(<MedicalReport patientData={patient} />);
    const canvas = await html2canvas(container, {
      scale: 1.5,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.7);

    const imgWidth = canvas.width * 0.75;
    const imgHeight = canvas.height * 0.75;

    const pdf = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: [imgWidth, imgHeight],
    });

    pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
    pdf.save("Medical_Receipt.pdf");

    root.unmount();
    document.body.removeChild(container);
  };

  const insightsKeys = [
    ...new Set(
      data?.flatMap((item) => item?.insights?.map((i) => i.key) || [])
    ),
  ];

  const insightColumns: any = insightsKeys.map((insightKey: any) => ({
    headerName: formatHeaderName(insightKey),
    field: insightKey,
    width: 150,
    resizable: true,
    cellRenderer: (params: any) => {
      if (insightKey === "preAssessmentQuestions") return;
      const insightValue = params?.data?.insights?.find(
        (item: any) => item?.key === insightKey
      )?.value;

      if (["alreadyTook", "needHumanIntervention"].includes(insightKey))
        return insightValue ? "Yes" : "No";

      if (!insightValue) return "--";
      if (isDateLike(insightValue))
        return converTime(insightValue?.trim().replace("t", "T"));

      if (insightKey === "testDates") {
        if (insightValue === "na") return insightValue;

        const testDates = JSON.parse(insightValue);

        return testDates?.length > 0 ? (
          <Box sx={{ maxHeight: "100px", ...commonStyles.hideScroll }}>
            {testDates.map((item: any, index: number) => (
              <Typography
                key={index}
                className="font-12-mediumgrey"
                sx={{ lineHeight: 1.8 }}
                textTransform="capitalize"
              >
                <strong>{item?.test}</strong>: {item?.date}
              </Typography>
            ))}
          </Box>
        ) : (
          "--"
        );
      }

      return insightValue;
    },
  }));

  const triggerConfirmHandler = (id) => {
    setJobId(id);
    setTriggerPop(true);
  };

  const triggerWorkflowHandler = async () => {
    setLoading(true);
    const obj = {
      jobId: jobId || "",
    };
    const res: any = await dispatch(celeryTaskPromoteAction(obj));
    if (res?.status === 200) {
      fetchLeadsData(false);
      setTriggerPop(false);
      setWorkflowPopup(true);
    }
    setLoading(false);
  };

  const handleDeletePopUp = (id) => {
    setPatientId(id);
    setConfirmationPopUp(true);
  };

  const deletePatientRow = async () => {
    setLoading(true);
    const res: any = await dispatch(deleteLeadsAction([patientId]));
    if (res?.status === 200) {
      const params = {
        pageIndex,
        pageSize: PAGE_SIZE_50,
        searchtext: "",
      };
      await dispatch(getLeadsDataAction(params));
      setConfirmationPopUp(false);
      setLoading(false);
      setDeletePopUp(true);
    }
  };

  const handleConfirmTrigger = async () => {
    setWorkflowTriggerAgainState((prevData: any) => ({
      ...prevData,
      confirmButtonLoading: true,
    }));
    const res: any = await dispatch(
      triggerAssessmentCallAction(triggerAgainLead, {})
    );
    if (res?.status === 200) {
      setWorkflowTriggerAgainState((prevData: any) => ({
        ...prevData,
        showTriggerConfirm: false,
        confirmButtonLoading: false,
      }));
      setWorkflowPopup(true);
    }
  };

  const exportAsTxt = async (data) => {
    if (Array.isArray(data?.insights)) {
      let preAssessment = data.insights.find(
        (item) => item.key === "preAssessmentQuestions"
      )?.value;

      if (typeof preAssessment === "string") {
        try {
          preAssessment = JSON.parse(preAssessment);
        } catch (err) {
          console.error("Invalid JSON in preAssessment:", err);
          return;
        }
      }
      if (Array.isArray(preAssessment)) {
        const questionAnswers: any = []; 

        preAssessment.forEach((item, index) => {
          const question = item?.question || "N/A";
          const answer = item?.answer || "N/A";

          questionAnswers.push(`Q${index + 1}: ${question}\nAns: ${answer}\n`);
        });

        const blob = new Blob([questionAnswers.join("\n")], {
          type: "text/plain",
        });
        const url = URL.createObjectURL(blob);
        const now = new Date();
        const timestamp = now
          .toISOString()
          .replace(/T/, "_")
          .replace(/:/g, "-")
          .replace(/\..+/, "");

        const a = document.createElement("a");
        a.href = url;
        a.download = `${data?.firstName || ""}_${
          data?.lastName || ""
        }_${timestamp}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  const columnsDef: ColDef[] = [
    {
      headerName: "Patient Id",
      field: "_id",
      pinned: "left",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?._id || "--",
    },
    {
      headerName: "First Name",
      field: "firstName",
      pinned: "left",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.firstName || "--",
    },
    {
      headerName: "Last Name",
      field: "lastName",
      pinned: "left",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.lastName || "--",
    },
    {
      headerName: "Email",
      field: "email",
      width: 200,
      resizable: true,
      valueGetter: (params) => params?.data?.email || "--",
    },
    {
      headerName: "Phone Number",
      field: "phoneNum",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.phoneNum || "--",
    },
    {
      headerName: "Date of Birth",
      field: "dateOfBirth",
      width: 150,
      resizable: true,
      valueFormatter: (params) => {
        return moment(params?.data?.dateOfBirth).format("DD-MM-YYYY") || "--";
      },
    },
    {
      headerName: "Insurance",
      field: "insurance",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.insurance || "--",
    },
    {
      headerName: "Pre-existing Condition",
      field: "preExistingCondition",
      width: 120,
      resizable: true,
      valueGetter: (params) => params?.data?.preExistingCondition || "--",
    },
    {
      headerName: "Date of Surgery",
      field: "dateOfSurgery",
      width: 120,
      resizable: true,
      valueGetter: (params) => params?.data?.dateOfSurgery || "--",
    },
    {
      headerName: "Time of Surgery",
      field: "arrivalTime",
      width: 120,
      resizable: true,
      valueGetter: (params) => params?.data?.arrivalTime || "--",
    },
    {
      headerName: "Record Type",
      width: 100,
      resizable: true,
      cellRenderer: (params) => {
        return (
          <BaseButton
            disabled={params?.data?.pdfRec?.length === 0}
            title="View"
            handleClick={() => setViewRecord(params?.data?.pdfRec)}
            styles={{ color: "#4280FF" }}
          />
        );
      },
    },
    {
      headerName: "Test Results Received",
      field: "testsDone",
      width: 120,
      resizable: true,
      cellRenderer: (params) =>
        renderTestResults(params?.data?.testsDone, "testsDone", params.data),
    },
    {
      headerName: "Tests Not Done",
      field: "testsNotDone",
      width: 150,
      resizable: true,
      cellRenderer: (params) =>
        renderTestResults(
          params?.data?.testsNotDone,
          "testsNotDone",
          params?.data
        ),
    },
    {
      headerName: "All Tests Needed",
      field: "actualTests",
      width: 150,
      resizable: true,
      cellRenderer: (params) =>
        renderTestResults(
          params?.data?.actualTests,
          "actualTests",
          params.data
        ),
    },
    {
      headerName: "Name Of Surgeon",
      field: "nameOfSurgeon",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.nameOfSurgeon || "--",
    },
    {
      headerName: "Email Of Surgeon",
      field: "emailOfSurgeon",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.emailOfSurgeon || "--",
    },
    {
      headerName: "Phone Number Of Surgeon",
      field: "phoneOfSurgeon",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.phoneOfSurgeon || "--",
    },
    ...insightColumns,
    {
      headerName: "Do Not Contact",
      field: "doNotContact",
      width: 150,
      resizable: true,
      valueGetter: (params) => (params?.data?.doNotContact ? "Yes" : "No"),
    },
    {
      headerName: "All Tests Done",
      field: "allTestsDone",
      width: 150,
      resizable: true,
      valueGetter: (params) => (params?.data?.allTestsDone ? "Yes" : "No"),
    },
    {
      headerName: "Arrival Time",
      field: "arrivalTime",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.arrivalTime || "--",
    },
    {
      headerName: "Procedure Time",
      field: "procedureTime",
      width: 150,
      resizable: true,
      valueGetter: (params) => params?.data?.procedureTime || "--",
    },
    {
      headerName: "Next Trigger",
      field: "",
      width: 150,
      resizable: true,
      valueGetter: (params: any) => {
        let taskFound = patientObj?.celeryData?.find(
          (locd) => locd?.data?.lead_id === params?.data?._id
        );
        let res;
        if (taskFound) {
          res = TimestampConverter(
            taskFound?.opts?.timestamp,
            taskFound?.opts?.delay
          );
        }
        return `${res?.formattedTime || "--"}- ${res?.userTimezone || "--"}`;
      },
    },
    {
      headerName: "Type",
      field: "flow",
      width: 120,
      resizable: true,
      valueGetter: (params) => params?.data?.flow || "--",
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 200,
      resizable: true,
      cellRenderer: (params) => {
        const preAssessmentValue = params?.data?.insights?.find(
          (item) => item.key === "preAssessmentQuestions"
        )?.value;

        const showExportAsTxtButton =
          preAssessmentValue && preAssessmentValue !== "na";
        const celeryObj = celeryData?.find(
          (locd) => locd?.data?.lead_id === params?.data?._id
        );
        const jobId = celeryObj?.id;
        return (
          <Box>
            <Box>
              <IconButton onClick={() => handleEdit(params.data)}>
                <EditIcon title="Edit Patient" />
              </IconButton>
              <IconButton
                title="Download H&P Report"
                onClick={() => handleDownloadPDF(params.data)}
              >
                <Download size={20} strokeWidth={1.5} />
              </IconButton>
              <Tooltip
                title={
                  jobId ? "Trigger workflow" : "Workflow already triggered"
                }
              >
                <span>
                  <IconButton
                    onClick={() => triggerConfirmHandler(jobId)}
                    disabled={loading || !jobId}
                  >
                    <Rocket size={20} strokeWidth={1.5} />
                  </IconButton>
                </span>
              </Tooltip>

              <IconButton
                onClick={() => {
                  setHistoryModal(true);
                  setCallHistory(params?.data?.history);
                }}
                title="Patient Call History"
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => handleDeletePopUp(params?.data?._id)}
                title="Delete Patient Data"
                disabled={loading}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            <Box>
              {params?.data?.doNotContact && (
                <IconButton title="Do not Contact" size="small">
                  <FlagIcon sx={{ color: "gray", fontSize: 20 }} />
                </IconButton>
              )}
              {params?.data?.insights?.find(
                (dat) =>
                  dat?.key === "needHumanIntervention" && dat?.value === true
              ) && (
                <IconButton title="Need Human Intervention" size="small">
                  <FlagIcon sx={{ color: "orange", fontSize: 20 }} />
                </IconButton>
              )}
              {params?.data?.allTestsDone ? (
                <IconButton title="All Tests Done" size="small">
                  <FlagIcon sx={{ color: "green", fontSize: 20 }} />
                </IconButton>
              ) : (
                <IconButton title="All Tests Not Done" size="small">
                  <FlagIcon sx={{ color: "red", fontSize: 20 }} />
                </IconButton>
              )}
              <BaseButton
                variant="text"
                title="Trigger Workflow Again"
                handleClick={() => {
                  setWorkflowTriggerAgainState((prevData: any) => ({
                    ...prevData,
                    showTriggerConfirm: true,
                    triggerAgainLead: params?.data?._id,
                  }));
                }}
                styles={{ color: "#4280FF" }}
              />
            </Box>
            {showExportAsTxtButton && (
              <BaseButton
                variant="text"
                title="Export as txt file"
                handleClick={() => exportAsTxt(params?.data)}
                styles={{ color: "#4280FF", textTransform: "none" }}
              />
            )}
          </Box>
        );
      },
    },
  ];

  const filtersObj = {
    patientObj,
    popoverOpen,
    handlePopoverClose,
    anchorEl,
    columnOrder,
    insightsKeys,
  };

  return (
    <>
      <Box sx={leadDataStyles.tableContainer}>
        <PatientTableHeader
          handlePopoverOpen={handlePopoverOpen}
          exportToCSV={exportToCSV}
          exportLoading={exportLoading}
          searchText={searchText}
          setPatinetState={setPatinetState}
        />

        <Box
          sx={{
            my: 2,
          }}
        >
          <ResizableGrid
            columns={columnsDef}
            allRows={data}
            pageIndex={pageIndex}
            setPatinetState={setPatinetState}
          />
        </Box>
      </Box>
      <PatientTableFilters filtersObj={filtersObj} />

      {viewRecord?.length > 0 && (
        <CommonModal
          show={viewRecord?.length > 0}
          body={
            <Box
              sx={{
                px: 3,
              }}
            >
              {viewRecord?.map((record: any) => (
                <Box key={record?.id} className="flex-between">
                  <Typography variant="body1">{record?.response}</Typography>
                  {record?.url && (
                    <BaseButton
                      title="View PDF"
                      handleClick={() => window.open(record?.url, "_blank")}
                      styles={{ color: "#4280FF" }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          }
          handleSave={false}
          showSaveButton={false}
          heading={`View Record`}
          size={500}
          toggle={() => setViewRecord([])}
        />
      )}

      {editRecordModal && (
        <CommonModal
          show={editRecordModal}
          body={
            <EditLeadModal
              formData={formData}
              setFormData={setFormData}
              testDone={testDone}
              setTestDone={setTestDone}
              handleChange={handleChange}
            />
          }
          handleSave={handleSave}
          heading={`Edit Record`}
          size={700}
          toggle={() => setEditRecordModal(false)}
          spin={editButtonLoading}
        />
      )}

      {patientPopup && (
        <CommonPopup
          message={`Patient is updated successfully`}
          show={patientPopup}
          toggle={() => setPatientPopup(false)}
          Icon={SuccessIcon}
          heading="Patient updated successfully"
        />
      )}

      {exportPopup && (
        <CommonPopup
          message={`The selected data are exported successfully`}
          show={exportPopup}
          toggle={() => setExportPopup(false)}
          Icon={ExportFileIcon}
          heading="File exported successfully"
        />
      )}
      {workflowPopup && (
        <CommonPopup
          message={`Workflow triggered successfully`}
          show={workflowPopup}
          toggle={() => setWorkflowPopup((prev) => !prev)}
          Icon={SuccessIcon}
          heading={`Workflow Triggered`}
        />
      )}

      {triggerPopUp && (
        <DeletePopup
          message={`Do you want to trigger workflow ?`}
          show={triggerPopUp}
          toggle={() => setTriggerPop((prev) => !prev)}
          handleDelete={triggerWorkflowHandler}
          spin={loading}
          headerText={"Workflow Trigger"}
          heading={`Trigger Workflow`}
          deleteText={"Trigger"}
          Icon={ConfirmationIcon}
        />
      )}

      {deletePopUp && (
        <CommonPopup
          message={`Patient removed successfully`}
          show={deletePopUp}
          toggle={() => setDeletePopUp((prev) => !prev)}
          Icon={SuccessIcon}
          heading={`Patient Removed`}
        />
      )}
      {confirmationPopUp && (
        <DeletePopup
          show={confirmationPopUp}
          toggle={() => setConfirmationPopUp((prev) => !prev)}
          message={"Are you sure you want to delete the selected patient ?"}
          handleDelete={deletePatientRow}
          spin={loading}
        />
      )}

      <ConfirmationPopup
        show={showTriggerConfirm}
        toggle={() =>
          setWorkflowTriggerAgainState((prevData: any) => ({
            ...prevData,
            showTriggerConfirm: false,
          }))
        }
        handleSave={handleConfirmTrigger}
        heading="Trigger Workflow"
        message="Are you sure you want to trigger workflow again?"
        confirmText="Trigger Workflow"
        Icon={ConfirmationIcon}
        spin={confirmButtonLoading}
      />

      {historyModal && (
        <CommonModal
          body={<PatientHistoryBody callHistoryData={callHistory} />}
          showSaveButton={false}
          heading={`Patient Call History`}
          show={historyModal}
          size={950}
          toggle={() => setHistoryModal((prev) => !prev)}
          handleSave={() => {}}
        />
      )}
    </>
  );
};

export default PatientTable;

const renderTestResults = (value, key, row: any = {}) => {
  const data = Array.isArray(value) ? value : JSON.parse(value || "[]");
  if (!Array.isArray(data) || data?.length === 0) {
    return "--";
  }

  return (
    <Box sx={{ maxHeight: "100px", ...commonStyles.hideScroll }}>
      {[...new Set(data)]?.map((item, index) => {
        const matchedPdf = row?.pdfRec?.find((pdf) => pdf.response === item);

        return (
          <Typography key={index} className="font-12-mediumgrey">
            {key === "testsDone" ? (
              <BaseButton
                disabled={!matchedPdf?.url}
                title={item}
                handleClick={() => window.open(matchedPdf?.url, "_blank")}
                styles={{ color: "#4280FF" }}
              />
            ) : (
              item
            )}
          </Typography>
        );
      })}
    </Box>
  );
};
